import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Global REQUEST interceptor: SEMPRE inietta il Bearer token dal localStorage.
// Elimina il classico bug "Not authenticated" quando axios.defaults.headers
// viene resettato (es. dopo un'importazione di modulo che clona axios, oppure
// dopo hot-reload in dev, o dopo che un'altra chiamata sovrascrive headers).
axios.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        // Non sovrascrivere se qualcuno ha gia' passato un Authorization esplicito
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (e) {
      // never break the request
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Global response interceptor: normalize FastAPI validation errors so that
// `error.response.data.detail` is ALWAYS a string. Without this, when FastAPI
// returns 422 with `detail` as an array of {type, loc, msg, input, url} objects,
// passing that array into toast.error / JSX crashes React (error #31 — blank page).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msg = detail
          .map((d) => {
            if (typeof d === 'string') return d;
            const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
            const text = d?.msg || d?.message || JSON.stringify(d);
            return loc ? `${loc}: ${text}` : text;
          })
          .join(' | ');
        error.response.data.detail = msg || 'Errore di validazione';
      } else if (detail && typeof detail === 'object') {
        error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
      }
    } catch (e) {
      // Never let the interceptor itself throw
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback (session_id in hash),
    // skip the /me check and let AuthCallback exchange the session_id first.
    // This prevents the race that logs the user out mid-flight and causes the
    // "gmail login is flaky" bug.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Errore durante il login' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.ruolo === roles;
    return roles.includes(user.ruolo);
  };

  const canAccess = (resource) => {
    if (!user) return false;
    
    const permissions = {
      super_admin: ['all'],
      admin: ['dashboard', 'leads', 'clients', 'contracts', 'sub_agents', 'users', 'export'],
      sub_agent: ['dashboard', 'leads', 'clients', 'contracts', 'profile'],
      back_office: ['all'],
      interno: ['all']
    };

    const userPermissions = permissions[user.ruolo] || [];
    return userPermissions.includes('all') || userPermissions.includes(resource);
  };

  const setAuthData = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      setAuthData,
      hasRole,
      canAccess,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
